import React, { createContext, useContext, useState, useEffect } from "react";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import { Navigate } from "react-router-dom";
import PropTypes from "prop-types";
import { auth } from "../../firebase";

// Create the AuthContext
const AuthContext = createContext();

// Hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Listen for authentication state changes
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return unsubscribe; // Cleanup the listener on unmount
  }, []);

  return (
    <AuthContext.Provider value={{ user }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

// Role-based route rendering with email verification check
export const RoleBasedRoute = ({ allowedRoles, fallbackRoute, children }) => {
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRole = async () => {
      const user = auth.currentUser;
      if (user) {
        // If the user's email is not verified, set the role to "public"
        if (!user.emailVerified) {
          setUserRole("public");
        } else {
          // If the email is verified, fetch the user's custom role from Firebase
          const idTokenResult = await getIdTokenResult(user);
          setUserRole(idTokenResult.claims.role || "public");
        }
      } else {
        setUserRole("public"); // If no user is logged in, set the role to "public"
      }
      setLoading(false);
    };

    fetchRole();
  }, []);

  if (loading) {
    return <div>Loading...</div>; // Optionally display a loader
  }

  // Check if the user's role matches the allowed roles
  if (allowedRoles.includes(userRole)) {
    return children; // Render the content for authorized roles
  } else {
    return <Navigate to={fallbackRoute} replace />;
  }
};

RoleBasedRoute.propTypes = {
  allowedRoles: PropTypes.array.isRequired,
  fallbackRoute: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};